<template>
  <LandingBackground>
    <div class="mb-20">
      <div class="mx-auto mt-10 grid grid-cols-1 items-start gap-10 sm:px-5 md:px-8 xl:grid-cols-2 3xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-10">
          <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
            <template #default>
              <LandingCardLinkSections label="Getting Started" v-if="hasNoOrganizations || hasNoFacilitySites">
                <LandingCardLinkSection
                  link-label="Request Access to an Organization"
                  description="If you need to submit recycling reports for a company or an agency, request access to an organization to get started. You will be notified after your request is reviewed and access is granted."
                  :to="{ name: routeNames.accessRequest.organization }"
                  v-if="hasNoOrganizations"
                />
                <LandingCardLinkSection
                  link-label="Request Access to a Facility"
                  description="If you need to submit disposal reports for a facility, request access to a facility to get started. You will be notified after your request is reviewed and access is granted."
                  :to="{ name: routeNames.accessRequest.facility }"
                  v-if="hasNoFacilitySites"
                />
              </LandingCardLinkSections>

              <LandingCardLinkSections label="Recycling Reports">
                <p class="text-sm leading-normal text-neutral-600">
                  For businesses and state agencies who submit annual recycling reports.
                </p>
                <PublicLandingList>
                  <PublicLandingListItem label="All Nevada counties are strongly encouraged to participate" />
                  <PublicLandingListItem
                    label="Counties with populations greater than 45,000 are required to participate"
                  />
                </PublicLandingList>
              </LandingCardLinkSections>

              <LandingCardLinkSections label="Disposal Reports">
                <p class="text-sm leading-normal text-neutral-600">
                  For solid waste management facilities who submit annual or quarterly solid waste tracking and disposal
                  reports.
                </p>
                <PublicLandingList>
                  <PublicLandingListItem
                    label="Facilities are assigned a reporting frequency (annual or quarterly) within their permit"
                  />
                  <PublicLandingListItem
                    label="Reports are due 30 days after the end of the calendar period (April 30, July 31, October 31, and January 31)"
                  />
                </PublicLandingList>
              </LandingCardLinkSections>

              <LandingCardLinkSections label="Questions">
                <p class="text-sm leading-normal text-neutral-600">
                  Contact Christian Pineda at
                  <a class="text-sky-600 hover:underline" href="tel:+17756879474">(775) 687-9474</a> or
                  <a class="text-sky-600 hover:underline" href="mailto:cpinedaarciniega@ndep.nv.gov"
                    >cpinedaarciniega@ndep.nv.gov</a
                  >.
                </p>
              </LandingCardLinkSections>
            </template>
          </LandingCard>
        </div>

        <div class="flex min-w-0 flex-col gap-10 xl:col-start-2 3xl:contents">
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-2">
            <LandingCard title="Organizations" :view-all-to="{ name: routeNames.organization.list }">
              <LandingCardSkeleton v-if="isGetOrganizationsPending" />
              <LandingCardEntityList :items="organizations?._items">
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      {{ item.name }}
                    </template>
                    <template #secondaryLabel>
                      <LookupDescription :murn="item.recyclingCategoryMurn" />
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.currentReport"
                      >
                        {{ item.currentReport.year }} Recycling Report
                        <Badge
                          :murn="item.currentReport.statusMurn"
                          :lookup-format="LookupFormat.description"
                          size="sm"
                          class="ml-2"
                          bordered
                        >
                        </Badge>
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
              <div class="border-t border-neutral-200 px-4 py-5 sm:px-6">
                <LandingCardLinkSection
                  link-label="Request Access to an Organization"
                  description="If you need to submit recycling reports for a company or an agency, request access to an organization to get started. You will be notified after your request is reviewed and access is granted."
                  :to="{ name: routeNames.accessRequest.organization }"
                />
              </div>
            </LandingCard>
          </div>

          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-3">
            <LandingCard title="Facilities" :view-all-to="{ name: routeNames.facilitySite.list }">
              <LandingCardSkeleton v-if="isGetFacilitySitesPending" />
              <LandingCardEntityList :items="facilitySites?._items">
                <template #default="{ item }">
                  <LandingCardEntityListItem :primary-label="item.name">
                    <template #secondaryLabel><LookupDescription :murn="item.typeMurn" /></template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
              <div class="border-t border-neutral-200 px-4 py-5 sm:px-6">
                <LandingCardLinkSection
                  link-label="Request Access to a Facility"
                  description="If you need to submit disposal reports for a facility, request access to a facility to get started. You will be notified after your request is reviewed and access is granted."
                  :to="{ name: routeNames.accessRequest.facility }"
                />
              </div>
            </LandingCard>
          </div>
        </div>
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import type { LandingCardEntity } from "@/components/landing/landingCard";
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { routeNames } from "@/routes";
import { createAccessRequest } from "@/views/accessRequest/accessRequestApi";
import type { FacilitySite } from "@/views/facilitySite/facilitySite";
import { getFacilitySites } from "@/views/facilitySite/facilitySiteApi";
import { type Organization } from "@/views/organization/organization";
import { getOrganizations } from "@/views/organization/organizationApi";
import { type RecyclingReport } from "@/views/recyclingReport/recyclingReport";
import { Badge, InitialBadge } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { NoneFoundFlag } from "@modernary/mui/components/common";
import { LookupDescription } from "@modernary/mui/components/lookup";
import { PublicLandingList, PublicLandingListItem } from "@modernary/mui/components/publicLanding";
import { LookupFormat } from "@modernary/mui/components/read";
import { type Paging, useApi } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { useAuthorization } from "@modernary/mui/utils/authorization";
import { noLabelSchema } from "@modernary/mui/utils/helpers";
import { computed, defineComponent, ref, watch } from "vue";

// Home content for unauth'd users
export default defineComponent({
  name: "HomeExternal",
  components: {
    LandingBackground,
    BaseButton,
    NoneFoundFlag,
    Badge,
    LookupDescription,
    LandingCard,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    PublicLandingList,
    PublicLandingListItem,
  },
  setup() {
    const { userRef } = useUser();
    const noLabel = noLabelSchema();

    const { authorizationClient, AuthRequest, handlePreempts } = useAuthorization();

    const isBureauAR = new AuthRequest();
    isBureauAR.roleName = "Bureau";

    const isHealthDistrictAR = new AuthRequest();
    isHealthDistrictAR.roleName = "HealthDistrict";

    const isBureau = ref(false);
    const isHealthDistrict = ref(false);
    authorizationClient
      .isAuthorizedArray([isBureauAR, isHealthDistrictAR])
      .then(([isBureauResult, isHealthDistrictResult]) => {
        isBureau.value = isBureauResult;
        isHealthDistrict.value = isHealthDistrictResult;
      }, handlePreempts);

    // Example usage of throttledAuthorize -
    // authorizationClient.throttledAuthorize(isBureauAR).then((result) => {
    //   isBureau.value = result?.isSuccess;
    // }, handlePreempts);

    // authorizationClient.throttledAuthorize(isHealthDistrictAR).then((result) => {
    //   isHealthDistrict.value = result?.isSuccess;
    // }, handlePreempts);

    const isBureauOrHd = computed(() => isBureau.value || isHealthDistrict.value);
    const isInternal = computed(
      () => Boolean(userRef.value?.claims?.some((x: any) => x.type === "role" && x.value === "Internal")), //TODO:TS
    );

    const isAuthenticated = computed(() => userRef.value.isAuthenticated);
    const isExternalAuthenticated = computed(() => isAuthenticated?.value && !isInternal.value);
    // const { recyclingCategories, facilitySiteTypes } = enums;

    type DashboardOrg = Organization & {
      currentReport: RecyclingReport;
    };
    const {
      dataRef: organizations,
      // statusRef,
      // setStatus,
      exec: execGetOrganizations,
      isStatusPending: isGetOrganizationsPending,
      isStatusSuccess: isGetOrganizationsSuccess,
    } = useApi(getOrganizations, {
      // initialData: [],
      responseAdapter: (response) => {
        const reportingYear = new Date().getFullYear() - 1;

        response.data._items.forEach((org: any) => {
          //TODO:TS
          org.currentReport = org.recyclingReports._items.find((x: any) => x.year === reportingYear); //TODO:TS
          org.to = { name: routeNames.organization.view, params: { id: org.id } };
        });

        return response.data as Paging<LandingCardEntity<DashboardOrg>>;
      },
    });

    const {
      dataRef: facilitySites,
      // statusRef,
      // setStatus,
      exec: execGetFacilitySites,
      isStatusPending: isGetFacilitySitesPending,
      isStatusSuccess: isGetFacilitySitesSuccess,
    } = useApi(getFacilitySites, {
      // initialData: [],
      responseAdapter: (response) => {
        response.data._items.forEach((fac: any) => {
          fac.to = { name: routeNames.facilitySite.view, params: { id: fac.id } };
        });

        return response.data as Paging<LandingCardEntity<FacilitySite>>;
      },
    });

    watch(
      isAuthenticated,
      (newValue) => {
        if (newValue) {
          execGetOrganizations({ expand: ["RecyclingReports"] });
          execGetFacilitySites();
        }
      },
      { immediate: true },
    );

    const hasNoOrganizations = computed(() => isGetOrganizationsSuccess.value && !organizations.value?._items?.length);

    const hasNoFacilitySites = computed(() => isGetFacilitySitesSuccess.value && !facilitySites.value?._items?.length);

    const rrAccessRequest = ref({ entityName: "", requestText: "" });
    rrAccessRequest.value.entityName = "Organization";

    const drAccessRequest = ref({ entityName: "", requestText: "" });
    drAccessRequest.value.entityName = "Facility Site";

    const formSubmissionCountRef = ref(0); //Used to "reset" the form field's state between submissions.
    const { exec: execCreateAccessRequest, isStatusPending } = useApi<any>(createAccessRequest, {
      initialData: { entityName: "", requestText: "" },
      responseAdapter: (response) => {
        if (response.data.wasRequestSubmitted) {
          rrAccessRequest.value.requestText = "";
          drAccessRequest.value.requestText = "";
          ++formSubmissionCountRef.value;
        }

        return response.data;
      },
    });

    const handleSubmit = (data: any) => {
      //TODO:TS
      return execCreateAccessRequest(data);
    };

    return {
      handleSubmit,
      noLabel,
      isExternalAuthenticated,
      isInternal,
      isBureauOrHd,
      isGetOrganizationsPending,
      isGetOrganizationsSuccess,
      organizations,
      userRef,
      isAuthenticated,
      drAccessRequest,
      rrAccessRequest,
      isStatusPending,
      formSubmissionCountRef,
      // recyclingCategories,
      BaseButton,
      isGetFacilitySitesPending,
      isGetFacilitySitesSuccess,
      getFacilitySites,
      facilitySites,
      // facilitySiteTypes,
      LookupFormat,
      routeNames,
      hasNoOrganizations,
      hasNoFacilitySites,
    };
  },
});
</script>
